/* jshint esnext: true */
import { $document, APP_NAME, $body, $html, isDebug, lang } from '../utils/environment';
import AbstractModule from './AbstractModule';

import { isObject } from '../utils/is';
import { store } from '../utils/store';

import { EVENT as APP_EVENT } from '../app';

import ProductRollBanner from './ProductRollBanner';
import ProductStringPennants from './ProductStringPennants';
import ProductFlags from './ProductFlags';
import ProductDressup from './ProductDressup';
import ProductOther from './ProductOther';

const MODULE_NAME = 'ProductManager';
const EVENT_NAMESPACE = `${APP_NAME}.${MODULE_NAME}`;

const EVENT = {
    CLICK: `click.${EVENT_NAMESPACE}`,
    TRIGGER: `trigger.ProductManager`,
    ONPRODUCTCONTENT: `onProductContent.ProductManager`,
    PAUSE: `pause.ProductManager`,
    TOGGLENAV: `toggle.Nav`
};

const CLASS = {
    ACTIVE: `is-active`,
    ONPRODUCTCONTENT: `on-product-content`,
    ONPRODUCTDETAILS: `on-product-details`,
    PRODUCTISLOADING: `product-is-loading`,
    SLIDERISPAUSED: `slider-is-paused`
};

const IDENTS = {
    'roll-banners': ProductRollBanner,
    'string-pennants': ProductStringPennants,
    'dress-up-kits': ProductDressup,
    'flags': ProductFlags,
    'others': ProductOther
};

export default class extends AbstractModule {
    constructor(options) {
        super(options);

        this.products          = new Array();
        this.isNavigable       = true;
        this.currentId         = (typeof options.currentid === 'string' && options.currentid !== '') ? options.currentid : null;
        this.autoplay          = this.currentId === null;
        this.currentIndex      = 0;
        this.previousIndex     = -1;
        this.firstBlood        = true;
        this.scrollIsOnDetails = false;
        this.sliderDelay       = 2000;
        this.sliderSpeed       = 5000;
        this.isBusy            = true;
        this.hasScene          = true;
        this.interval;
    }

    init() {

        if (this.detectWebGL() === -1) {
            this.hasScene = false;
            this.stopRenderer();
        }

        this.initScene();

        this.$list      = $('.js-list', this.$el);
        this.$slides    = $('.js-slider-item', this.$el);
        this.$buttons   = $('.js-product-button');
        this.$listItems = $('.js-product-list-item');
        this.$counter   = $('.js-product-slider-counter');
        this.$content   = $('.js-product-content-container');
        this.slideCount = this.$slides.length;

        const initialIndex = this.$listItems.filter(`.${CLASS.ACTIVE}`).index();
        if (initialIndex !== -1) {
            this.currentIndex = initialIndex;
        }

        this.$scrollContainer = $('#js-scroll');

        if (this.hasScene) {
            let i = 0;
            for (; i < this.slideCount; i++) {
                this.products.push(new IDENTS[this.$slides.eq(i).attr('data-ident')](this.scene));
            }
        }

        if (!this.autoplay) {
            $body.addClass(CLASS.SLIDERISPAUSED);
        }

        this.$el.on(EVENT.CLICK, '.js-next', (event) => {
            if (this.isNavigable) {
                this.isNavigable = false;

                this.increment();

                if (!$body.hasClass(CLASS.SLIDERISPAUSED) && this.autoplay) {
                    clearInterval(this.interval);
                    this.play();
                }

                setTimeout(() => {
                    this.isNavigable = true;
                }, this.sliderDelay);
            }
        });

        this.scrollableWave = true;

        $document
            .on(EVENT.PAUSE, (event) => {
                this.setPause();
            })
            .on(EVENT.TRIGGER, (event) => {
                if (event.way === 'enter') {
                    this.setPause();
                    $body.addClass(CLASS.ONPRODUCTDETAILS);
                    this.scrollIsOnDetails = true;

                    if (window.scroll.direction == 'down') {
                        if (this.scrollableWave && !$html.hasClass('scrolling-to')){
                            this.$scrollContainer.triggerHandler({
                                type: 'scrollTo.LocomotiveScroll',
                                options: {
                                    targetElem: this.$content
                                }
                            });
                            this.scrollableWave = false;

                            setTimeout(() => {
                                this.scrollableWave = true;
                            }, 1000);
                        }
                    }
                } else if (event.way === 'leave') {
                    if (window.scroll.direction == 'up') {
                        this.setPlay();
                        $body.removeClass(CLASS.ONPRODUCTDETAILS);
                        this.scrollIsOnDetails = false;
                    }
                }
            })
            .on(EVENT.ONPRODUCTCONTENT, (event) => {
                if (event.way === 'enter') {
                    $body.addClass(CLASS.ONPRODUCTCONTENT);
                    this.stopRenderer();
                } else if (event.way === 'leave') {
                    this.playRenderer();
                    $body.removeClass(CLASS.ONPRODUCTCONTENT);

                    if (this.scrollableWave && !$html.hasClass('scrolling-to')){
                        this.$scrollContainer.triggerHandler({
                            type: 'scrollTo.LocomotiveScroll',
                            options: {
                                targetElem: this.$el
                            }
                        });
                        this.scrollableWave = false;

                        setTimeout(() => {
                            this.scrollableWave = true;
                        }, 1000);
                    }
                }
            })
            .on(EVENT.CLICK, '.js-product-button', (event) => {
                const index = $(event.currentTarget).parent().index();

                if (this.isNavigable && this.currentIndex !== index) {
                    this.isNavigable = false;
                    this.previousIndex = this.currentIndex;
                    this.currentIndex = index;

                    this.changeActiveProduct(true);

                    setTimeout(() => {
                        this.isNavigable = true;
                    }, this.sliderDelay);

                    if (!$body.hasClass(CLASS.SLIDERISPAUSED) && this.autoplay) {
                        clearInterval(this.interval);
                        this.play();
                    }
                }
            })
            .on(EVENT.TOGGLENAV, (event) => {
                if (event.option === 'open') {
                    if (this.autoplay) {
                        this.setPause();
                    }
                    this.stopRenderer();
                } else {
                    this.setPlay();
                    this.playRenderer();
                }
            });

        setTimeout(() => {
            this.setPlay();
            this.changeActiveProduct();
        }, this.sliderDelay);
    }

    /**
     * Launch playing mode and managed related state classes.
     *
     * @return {void}
     */
    setPlay() {
        if (this.autoplay) {
            $body.removeClass(CLASS.SLIDERISPAUSED);
            this.play();
        }
    }

    /**
     * Launch the slider interval.
     *
     * @return {void}
     */
    play() {
        this.interval = setInterval(() => this.increment(), this.sliderSpeed);
    }

    /**
     * Manage the index and previous index values.
     *
     * @return {void}
     */
    increment() {
        this.previousIndex = this.currentIndex;

        if (this.currentIndex >= this.slideCount - 1) {
            this.currentIndex = 0;
        } else {
            this.currentIndex++;
        }

        this.changeActiveProduct();
    }

    /**
     * Pause the slider and manage class on body and active classes
     *
     * @return {void}
     */
    setPause() {
        clearInterval(this.interval);
        $body.addClass(CLASS.SLIDERISPAUSED);
    }

    /**
     * Initialize the WebGL scene (camera, floor and lighting).
     *
     * @return void
     */
    initScene() {
        if (this.hasScene) {
            this.scene,
            this.camera,
            this.renderer,
            this.element,
            this.container = null;

            this.$scene = this.$el.find('#js-scene');
            this.scene = new THREE.Scene();

            if (isDebug) {
                window.scene = this.scene;
            }

            this.camera = new THREE.OrthographicCamera(
                window.innerWidth / - 2,
                window.innerWidth / 2,
                window.innerHeight / 2,
                window.innerHeight / - 2,
                0.01,
                2000
            );
            this.camera.position.y = 600;
            this.camera.position.z = 600;
            this.camera.position.x = 600;
            this.camera.updateProjectionMatrix();
            this.camera.lookAt(this.scene.position);

            this.scene.add(this.camera);

            if (window.isIos) {
                this.renderer = new THREE.WebGLRenderer({ alpha: true });
            } else {
                this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
            }

            this.renderer.setPixelRatio(window.devicePixelRatio);
            this.renderer.shadowMap.enabled = true;
            this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
            this.element = this.renderer.domElement;
            this.container = this.$scene[0];

            this.gl = this.element.getContext("experimental-webgl", { stencil: true });

            this.maxRenderBufferSize = this.gl.getParameter( this.gl.MAX_RENDERBUFFER_SIZE);
            window.fullWebgl = true;

            if (this.maxRenderBufferSize <= 8192) {
                window.fullWebgl = false;
            }

            this.container.width = this.$scene.outerWidth();
            this.container.height = this.$scene.outerHeight();

            this.element.width = this.container.width;
            this.element.height = this.container.height;

            this.renderer.setSize(this.container.width, this.container.height);

            this.container.appendChild(this.element);

            this.floorGeometry = new THREE.PlaneBufferGeometry(3000, 3000, 1, 1);
            this.floorMaterial = new THREE.ShadowMaterial();
            this.floorMaterial.opacity = 0.2;
            this.floor = new THREE.Mesh(this.floorGeometry, this.floorMaterial);
            this.floor.receiveShadow = true;
            this.floor.position.set(0, 0, 0);
            this.floor.rotation.x = -90 * Math.PI / 180;

            this.scene.add(this.floor);

            this.renderer.shadowMap.renderReverseSided = false;

            // Lighting
            this.light = new THREE.SpotLight(0xffffff, 2.6);
            // Default; light shining from top
            this.light.position.set(window.innerWidth * 2, window.innerWidth * 2.5, 0);
            // Default; false
            this.light.castShadow = true;

            this.light.shadow = new THREE.LightShadow(
                new THREE.OrthographicCamera(
                    -window.innerWidth,
                    window.innerWidth,
                    window.innerHeight,
                    -window.innerHeight,
                    1,
                    3000
                )
            );
            this.light.shadow.camera.near = 0.01;
            this.light.shadow.bias = 0.1;
            this.light.shadow.camera.far = 20000;
            this.light.shadow.mapSize.width = 4084;
            this.light.shadow.mapSize.height = 4084;
            this.scene.add(this.light);

            this.ambientLight = new THREE.AmbientLight(0xffffff,1);
            this.scene.add(this.ambientLight);

            this.playRenderer();
        }
    }

    /**
     * Launch the rendering loop.
     *
     * @return {void}
     */
    playRenderer() {
        if (this.hasScene) {
            this.raf = requestAnimationFrame(() =>this.playRenderer());
            // console.log(this.renderer);
            this.renderer.render(this.scene, this.camera);
        }
    }

    /**
     * Stop the rendering loop.
     *
     * @return {void}
     */
    stopRenderer() {
        if (this.hasScene) {
            cancelAnimationFrame(this.raf);
        }
    }

    /**
     * Update WebGL scene when window is resized.
     *
     * @return {void}
     */
    resize() {
        this.container.width = this.$scene.outerWidth();
        this.container.height = this.$scene.outerHeight();
        this.element.width = this.container.width;
        this.element.height = this.container.height;

        let width = this.container.width;
        let height = this.container.height;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
    }

    /**
     * Change active slide and update the ProductRange content.
     *
     * @param  {Boolean} render - Whether to render the Scroll instance when content is injected.
     * @return {void}
     */
    changeActiveProduct(render) {
        const $currentSlide = this.$slides.eq(this.currentIndex);

        this.loadContent({
            id: $currentSlide.attr('data-id'),
            render: (render !== false && render !== true) ? false : render,
            colorId: $currentSlide.attr('data-colorId')
        });

        if (this.hasScene) {
            // Alpha transition of the floor
            const floorOpacityTimeline = new TimelineMax();

            // Weird condition?
            const floorOpacityTimelineDuration = (this.currentIndex === 3) ? 0 : 0.3;

            floorOpacityTimeline.to(this.floor.material, floorOpacityTimelineDuration, {
                opacity: 0
            });

            floorOpacityTimeline.to(this.floor.material, 0.3, {
                opacity: 0.2,
                delay: 1
            });

            if ($currentSlide.attr('data-ident') === 'string-pennants') {
                TweenMax.to(this.light.position, 0.6, {
                    y: window.innerWidth * 0.8
                });
            } else {
                TweenMax.to(this.light.position, 0.6, {
                    y: window.innerWidth * 2.5
                });
            }
        }

        TweenMax.to(this.$el, 1, {
            css: {
                backgroundColor: $currentSlide.attr('data-color')
            },
            onComplete: () => {
                if (this.hasScene) {
                    // Manage floor rotation
                    if ($currentSlide.attr('data-ident') === 'string-pennants') {
                        this.floor.position.set(-window.innerWidth / 2, 0, 0);
                        this.floor.rotation.y = 90 * Math.PI / 180;
                    } else {
                        this.floor.position.set(0, 0, 0);
                        this.floor.rotation.y = 0;
                    }
                }
            }
        });

        TweenMax.to(this.$counter, 0.6, {
            y: `${-(this.currentIndex * 100) / this.slideCount} %`
        });

        TweenMax.to(this.$list, 0.6, {
            x: `${-(this.currentIndex * 100) / this.slideCount} %`
        });

        // If it's not the first slide change, hide the old slide
        if (!this.firstBlood) {
            this.$listItems.removeClass(CLASS.ACTIVE);
            this.$slides.removeClass(CLASS.ACTIVE);
            if (this.hasScene) {
                this.products[this.previousIndex].hide();
            }
        } else {
            this.firstBlood = false;
        }

        if (this.hasScene) {
            this.products[this.currentIndex].init();
        }

        this.$listItems.eq(this.currentIndex).addClass(CLASS.ACTIVE);
        $currentSlide.addClass(CLASS.ACTIVE);
    }

    /**
     * Load the content partial according to a ProductRange slide.
     *
     * Promises allow us to wait until ajax query is complete before returning.
     * Local storage allows us to not query the server.
     *
     * @param  {Object} options Data from the slide.
     * @return void
     */
    loadContent(options) {
        // Prevent any loading if the current ProductRange ID is the same as the one requested.
        // This happens when viewing a ProductRange URL.
        if (this.currentId !== options.id) {
            this.currentId = options.id;
            $body.addClass(CLASS.PRODUCTISLOADING);

            let content = store.get(this.storageKey(options.id)) || null;

            // Check local storage cache before querying the server.
            if (content !== null) {

                if (this.scrollIsOnDetails) {
                    this.$scrollContainer.triggerHandler({
                        type: 'scrollTo.LocomotiveScroll',
                        options: {
                            targetElem: this.$content
                        }
                    });
                }

                this.displayContent(options);
            } else {
                const guard = (response, textStatus, jqXHR) => {
                    if (!response || !response.success) {
                        return $.Deferred().reject(jqXHR, textStatus, 'Bad HTTP Response Structure');
                    }

                    return $.Deferred().resolve(response, textStatus, jqXHR);
                };

                const request = {
                    url:      `render/product-range/${options.id}`,
                    method:   'GET',
                    dataType: 'json'
                };

                const xhr = $.ajax(request).then(guard).done((response) => {
                    if (response.success === true) {
                        const expiresAt = new Date();
                        expiresAt.setHours(expiresAt.getHours() + 1);
                        store.set(this.storageKey(options.id), response.results, expiresAt);
                    }

                    this.displayContent(options);
                });
            }
        } else {
            this.applyColorId(options.colorId);;
        }
    }
    /**
     * Display active ProductRange content and toggle any related classes.
     *
     * @param  {Object} options Data from the slide.
     * @return {void}
     */
    displayContent(options) {
        setTimeout(() => {
            this.applyColorId(options.colorId);
        }, 600);

        setTimeout(() => {
            $document.triggerHandler({
                type:   APP_EVENT.DELETE_SCOPED_MODULES,
                $scope: this.$content
            });

            this.$content.html(store.get(this.storageKey(options.id)));

            $document.triggerHandler({
                type:   APP_EVENT.INIT_SCOPED_MODULES,
                $scope: this.$content
            });

            setTimeout(() => {
                this.$scrollContainer.triggerHandler({
                    type: 'update.LocomotiveScroll'
                });
            },1000);

            $body.removeClass(CLASS.PRODUCTISLOADING);

            // If the user is on details, scroll
            if (this.scrollIsOnDetails) {

                // Force render on navigation click
                // Edge case where, when content is loaded and rendered, a user clicks another product in menu.
                // No scrolling happens since we're at the right Y position, therefore not rendering the content.
                if (options.render) {
                    setTimeout(() => {
                        this.$scrollContainer.triggerHandler({
                            type: 'update.LocomotiveScroll'
                        });
                        this.$scrollContainer.triggerHandler({
                            type: 'render.LocomotiveScroll'
                        });
                    },1000);
                }
            }
        }, 500);
    }

    /**
     * Set a color ident on the body element.
     *
     * @param  {string} id A color identification.
     * @return {void}
     */
    applyColorId(id) {
        $body.attr('data-colorId', id);
    }

    /**
     * Retrieve the product manage's storage keypath.
     *
     * @param  {string} keypath - The keypath.
     * @return {string}
     */
    storageKey(keypath) {
        return `productManager/${lang()}/${keypath}`;
    }

    detectWebGL() {
        // Check for the WebGL rendering context
        if (!!window.WebGLRenderingContext) {
            let canvas = document.createElement("canvas"),
                names = ["webgl", "experimental-webgl", "moz-webgl", "webkit-3d"],
                context = false;

            for (var i in names) {
                try {
                    context = canvas.getContext(names[i]);
                    if (context && typeof context.getParameter === "function") {
                        // WebGL is enabled.
                        return 1;
                    }
                } catch (e) {}
            }

            // WebGL is supported, but disabled.
            return 0;
        }

        // WebGL not supported.
        return -1;
    }

    destroy() {
        super.destroy();
        clearInterval(this.interval);
        this.stopRenderer();
        this.scene = null;
        this.renderer = null;
        $body.data('colorId','')
        $body.removeClass(`${CLASS.PRODUCTISLOADING} ${CLASS.SLIDERISPAUSED} ${CLASS.ONPRODUCTDETAILS} ${CLASS.ONPRODUCTCONTENT}`);
        this.$el.off(`.${EVENT_NAMESPACE}`);
        $document.off(`.${EVENT_NAMESPACE}`);
        $document.off(`.ProductManager`);
        $document.off(`.Nav`);
    }
}
